/* KBW filter rail — the behaviour every filter sidebar shares.

   Custom script: not part of the Maximize theme, so a theme update never
   overwrites it. Styles: assets/kbw-filter-rail.css.

   Only presentation lives here. What a tick does — hide cards in the page, or
   ask Shopify for a different set of products — stays with each list's own
   script. This file:

   - keeps `--kbw-rail-top` equal to the header's bottom edge, so the sticky
     rail sits under a header that hides on the way down and returns on the
     way up;
   - opens and closes the rail as a drawer below 1024px (scrim, close button,
     "Show results", Escape; focus kept inside, the page behind made inert);
   - "Show more" on a clipped option list;
   - the ticked-count badges on group titles and on the Filters button, for
     rails whose ticks are real checkboxes.

   Everything is delegated from the document, so a list that swaps its own
   markup in (the cabinet list does, on every facet click) needs no second
   set-up. Each list's section prints the script tag, so it can arrive more
   than once; the guard below runs it once.

   Markup hooks:
     [data-kbw-rail]            the rail box (has an id)
     [data-kbw-rail-open]       the Filters button, aria-controls = rail id
     [data-kbw-rail-close]      close button inside the rail
     [data-kbw-rail-apply]      "Show results" inside the rail
     [data-kbw-rail-scrim]      the backdrop, a sibling in .kbw-rail-layout
     [data-kbw-rail-more]       "Show N more", right after its option list
     [data-kbw-rail-options]    an option list; data-limit (default 8)
     [data-kbw-rail-badge]      the Filters button's count
   Public: window.KbwRail.refresh(root) re-clips lists and recounts badges
   after a list's script has ticked, unticked or reordered options;
   window.KbwRail.sync() after markup has been swapped in. */

(function () {
  'use strict';

  if (window.KbwRail) return;

  var wide = window.matchMedia ? window.matchMedia('(min-width: 1024px)') : null;
  var supportsInert = 'inert' in HTMLElement.prototype;
  var openId = '';
  var opener = null;
  var inerted = [];

  /* --- Sticky offset ----------------------------------------------------------- */

  var headerless = false;
  function readHeader() {
    if (headerless) return;
    var h = document.querySelector('.section-header') || document.querySelector('header');
    if (!h) { headerless = true; return; }
    var bottom = h.getBoundingClientRect().bottom;
    if (bottom < 0) bottom = 0;
    document.documentElement.style.setProperty('--kbw-rail-top', Math.round(bottom) + 'px');
  }
  var queued = false;
  function queueHeader() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; readHeader(); });
  }
  window.addEventListener('scroll', queueHeader, { passive: true });
  window.addEventListener('resize', queueHeader, { passive: true });

  /* --- Show more ------------------------------------------------------------------ */

  function isTicked(opt) {
    if (opt.classList.contains('is-active')) return true;
    var input = opt.querySelector('input');
    return !!(input && input.checked);
  }

  /* Lists printed by the server arrive already clipped. A list whose options
     were ticked from the address or reordered by its own script is clipped
     again here, from the order it now has; a ticked value is never hidden. */
  function clip(list) {
    var more = list.nextElementSibling;
    if (!more || !more.hasAttribute('data-kbw-rail-more')) return;
    if (list.getAttribute('data-kbw-rail-expanded') === '1') return;
    var limit = parseInt(list.getAttribute('data-limit'), 10) || 8;
    var opts = Array.prototype.slice.call(list.children);
    var hidden = 0;
    opts.forEach(function (opt, i) {
      var over = i >= limit && !isTicked(opt);
      opt.classList.toggle('is-over', over);
      if (over) hidden += 1;
    });
    list.classList.toggle('is-clipped', hidden > 0);
    more.hidden = hidden === 0;
    if (hidden > 0) more.textContent = 'Show ' + hidden + ' more';
  }

  /* --- Badges ---------------------------------------------------------------------- */

  function countBadges(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-kbw-rail]'), function (rail) {
      var total = 0;
      Array.prototype.forEach.call(rail.querySelectorAll('.kbw-rail__group'), function (group) {
        var badge = group.querySelector('[data-kbw-rail-groupbadge]');
        if (!badge) return;
        var n = group.querySelectorAll('.kbw-rail__input:checked').length;
        total += n;
        badge.textContent = n;
        badge.hidden = n === 0;
      });
      if (!rail.id) return;
      var btnBadge = document.querySelector('[data-kbw-rail-open][aria-controls="' + rail.id + '"] [data-kbw-rail-badge]');
      if (btnBadge) {
        btnBadge.textContent = total;
        btnBadge.hidden = total === 0;
      }
    });
  }

  function refresh(root) {
    root = root || document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-kbw-rail-options]'), clip);
    countBadges(root);
  }

  /* --- The drawer ------------------------------------------------------------------- */

  function currentRail() {
    return openId ? document.getElementById(openId) : null;
  }

  function scrimFor(rail) {
    var layout = rail.closest('.kbw-rail-layout');
    return layout ? layout.querySelector('[data-kbw-rail-scrim]') : null;
  }

  function focusables(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      ))
      .filter(function (el) { return el.getClientRects().length > 0; });
  }

  /* Everything outside the rail and its scrim drops out of the accessibility
     tree while the drawer is open. Only ancestors of the rail are walked into,
     so the cost follows how deep the rail sits, not how big the page is. */
  function setInert(rail, on) {
    inerted.forEach(function (el) {
      if (supportsInert) el.inert = false;
      else el.removeAttribute('aria-hidden');
    });
    inerted = [];
    if (!on || !rail) return;
    var keep = [rail, scrimFor(rail)];
    var walk = function (container) {
      Array.prototype.forEach.call(container.children, function (child) {
        var holds = keep.some(function (k) { return k && (child === k || child.contains(k)); });
        if (holds) {
          if (keep.indexOf(child) === -1) walk(child);
          return;
        }
        if (supportsInert) child.inert = true;
        else child.setAttribute('aria-hidden', 'true');
        inerted.push(child);
      });
    };
    walk(document.body);
  }

  function show(rail, moveFocus) {
    var scrim = scrimFor(rail);
    rail.classList.add('is-open');
    rail.setAttribute('role', 'dialog');
    rail.setAttribute('aria-modal', 'true');
    var title = rail.querySelector('.kbw-rail__title');
    if (title && title.id) rail.setAttribute('aria-labelledby', title.id);
    if (scrim) scrim.hidden = false;
    document.body.style.overflow = 'hidden';
    setInert(rail, true);
    var btn = document.querySelector('[data-kbw-rail-open][aria-controls="' + rail.id + '"]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    if (!moveFocus) return;
    /* The rail is `visibility: hidden` until the class has rendered, and a
       hidden element takes no focus — two frames first. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var close = rail.querySelector('[data-kbw-rail-close]');
        if (rail.classList.contains('is-open') && close) close.focus();
      });
    });
  }

  function open(rail, btn) {
    if (!rail || (wide && wide.matches)) return;
    openId = rail.id;
    opener = btn || null;
    show(rail, true);
  }

  function close(returnFocus) {
    var rail = currentRail();
    if (rail) {
      rail.classList.remove('is-open');
      rail.removeAttribute('role');
      rail.removeAttribute('aria-modal');
      rail.removeAttribute('aria-labelledby');
      var scrim = scrimFor(rail);
      if (scrim) scrim.hidden = true;
      var btn = document.querySelector('[data-kbw-rail-open][aria-controls="' + rail.id + '"]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (returnFocus !== false) {
        if (btn) btn.focus();
        else if (opener && document.contains(opener)) opener.focus();
      }
    }
    setInert(null, false);
    document.body.style.overflow = '';
    openId = '';
    opener = null;
  }

  /* A list that swaps its markup in while the drawer is open gets a fresh,
     closed rail. The customer was in the middle of choosing, so the drawer is
     put back open over the new markup; if the rail has gone, the page is
     released. */
  function sync() {
    if (!openId) return;
    var rail = currentRail();
    if (!rail) { close(false); return; }
    if (!rail.classList.contains('is-open')) show(rail, true);
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var more = t.closest('[data-kbw-rail-more]');
    if (more) {
      var list = more.previousElementSibling;
      if (list) {
        list.classList.remove('is-clipped');
        list.setAttribute('data-kbw-rail-expanded', '1');
      }
      more.hidden = true;
      return;
    }

    var openBtn = t.closest('[data-kbw-rail-open]');
    if (openBtn) {
      open(document.getElementById(openBtn.getAttribute('aria-controls')), openBtn);
      return;
    }

    if (t.closest('[data-kbw-rail-close], [data-kbw-rail-apply], [data-kbw-rail-scrim]')) {
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    var rail = currentRail();
    if (!rail || !rail.classList.contains('is-open')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var items = focusables(rail);
    if (!items.length) { e.preventDefault(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (items.indexOf(document.activeElement) === -1) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    }
  });

  /* Ticks made by hand. A list's own script calls refresh() for the ones it
     makes itself (from the address, or Reset). */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.classList && t.classList.contains('kbw-rail__input')) {
      countBadges(t.closest('.kbw-rail-layout') || document);
    }
  });

  /* Turning a tablet to landscape can cross into the wide layout with the
     drawer open, which would leave the page unable to scroll. */
  if (wide) {
    var onWide = function (ev) { if (ev.matches && openId) close(false); };
    if (wide.addEventListener) wide.addEventListener('change', onWide);
    else if (wide.addListener) wide.addListener(onWide);
  }

  window.KbwRail = { refresh: refresh, sync: sync, close: close };

  function start() {
    readHeader();
    refresh(document);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', function (e) { refresh(e.target || document); });
})();
